import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const FAQS = [
  {
    q: "How much does a car remap cost in Nottingham or Derby?",
    a: "Stage 1 remaps typically range £250–£400 for petrol and £275–£425 for diesel, depending on your vehicle. Stage 2 and Stage 3 pricing depends on the hardware fitted. Every quote is fixed up-front — no surprises, no hidden bench-writing fees.",
  },
  {
    q: "Is car remapping legal in the UK?",
    a: "Yes. ECU remapping is legal for road use provided the vehicle still passes MOT and emissions testing. You are legally required to declare the modification to your insurer — most mainstream insurers accept single-stage remaps with a small premium adjustment.",
  },
  {
    q: "Will a remap void my manufacturer warranty?",
    a: "It can — a dealer may refuse to warranty a component that has been subjected to remapped power. Mids Tuning archives your original stock file, so a full return-to-factory is always possible before any dealer visit. Ask us for advice on your specific vehicle.",
  },
  {
    q: "How long does a mobile ECU remap take?",
    a: "Most Stage 1 remaps take 45–90 minutes on-site including a full pre-map data-log, the file write, and a post-map verification drive. Stage 2 and 3 calibrations can take longer depending on the extent of live-road tuning required.",
  },
  {
    q: "Do you remap diesels?",
    a: "Yes. We specialise in road-legal diesel Stage 1 and Stage 2 maps that keep every emissions component fully functional and MOT-compliant. For off-road-only DPF, EGR or AdBlue solutions, we work diagnostics-first: we find and fix the underlying fault before any file is touched.",
  },
  {
    q: "What power gains can I expect?",
    a: "Petrol turbocharged cars typically gain 25–30% BHP and 35% torque on Stage 1. Diesel cars often see the biggest torque uplift — up to 50% on Stage 2. Use our vehicle calculator on the homepage for an evidence-based estimate specific to your make, model and year.",
  },
  {
    q: "Is remapping safe for my engine?",
    a: "When calibrated properly, yes. We use conservative boost and AFR targets on daily-driven cars, monitor knock live, and stay well inside factory hardware limits on stock builds. Every calibration is written to your VIN — no generic 'cloud files' — and comes with a 30-day peace-of-mind warranty.",
  },
  {
    q: "Do you cover my area outside Nottingham and Derby?",
    a: "We cover the entire East Midlands including Long Eaton, Beeston, Arnold, Ilkeston, Ripley, Belper, Loughborough, Mansfield and Chesterfield. Message us with your postcode — if it's within reach we'll bring the tools to you.",
  },
  {
    q: "Do I need any hardware upgrades before booking?",
    a: "No — Stage 1 is designed for a completely stock car and requires nothing more than a healthy engine. Hardware only becomes necessary if you want to progress to Stage 2 or beyond. We'll advise honestly based on your goals.",
  },
  {
    q: "How do I book?",
    a: "Use the booking form on our homepage or send us a message via Facebook. We'll come back within one working day with a firm quote, an available slot and the exact scope of work for your vehicle.",
  },
];

function FaqItem({ item, index, open, onToggle }) {
  return (
    <div
      className="border-t border-white/10"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-6 py-6 md:py-8 text-left group"
        aria-expanded={open}
      >
        <div className="flex items-baseline gap-5">
          <span className="font-mono text-[11px] uppercase tracking-widest text-volt shrink-0">
            /{String(index + 1).padStart(2, "0")}
          </span>
          <h3 className="font-display uppercase text-lg md:text-2xl lg:text-3xl leading-tight tracking-tight text-white group-hover:text-volt transition-colors">
            {item.q}
          </h3>
        </div>
        <span className="shrink-0 mt-2 text-white/60 group-hover:text-volt transition-colors">
          {open ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
        className="overflow-hidden"
      >
        <p className="pb-8 md:pl-16 font-body text-white/70 text-base md:text-lg leading-relaxed max-w-3xl">
          {item.a}
        </p>
      </motion.div>
    </div>
  );
}

export default function Faq() {
  const [openIndex, setOpenIndex] = useState(0);

  // Inject FAQPage JSON-LD for Google rich results
  useEffect(() => {
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "faq-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    const existing = document.getElementById("faq-jsonld");
    if (existing) existing.remove();
    document.head.appendChild(ld);
    return () => {
      const t = document.getElementById("faq-jsonld");
      if (t) t.remove();
    };
  }, []);

  return (
    <section
      id="faq"
      className="relative bg-carbon py-24 md:py-32 px-6 md:px-10 border-t border-white/10"
      data-testid="faq-section"
      aria-labelledby="faq-heading"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-volt mb-6">
            ◉ FAQ · Answers
          </p>
          <h2
            id="faq-heading"
            className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tighter"
          >
            Everything you <br />
            <span className="text-white/40">need to know.</span>
          </h2>
          <p className="font-body text-white/60 text-base leading-relaxed mt-8 max-w-sm">
            The questions Nottingham and Derby drivers ask us most about
            <strong className="text-white"> car remapping, ECU tuning</strong> and Stage 1/2/3 mapping.
          </p>
        </div>

        <div className="col-span-12 md:col-span-8">
          {FAQS.map((f, i) => (
            <FaqItem
              key={f.q}
              item={f}
              index={i}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
